/**
 * The "the tenant changed" seam — story 0.5 AC-9.
 *
 * The original AC said switching client must "re-resolve the feature flags
 * (integrates with 0.4)". But **0.4 runs after 0.5**, so there are no flags to
 * re-resolve yet. Rather than defer the AC, the dependency is inverted, exactly as
 * `auth/sessionBridge.ts` inverts logout teardown: 0.5 owns the event, and whoever
 * needs to react registers a listener.
 *
 * 🔒 Points ONE way, for the same reason the session bridge does: `tenant/` must
 * not import `flags/` or `api/`. If it did, the tenant layer would acquire a
 * dependency on everything that reacts to it, and the layering would invert.
 */
type AoTrocar = (idCliente: string) => void | Promise<void>

const ouvintes = new Set<AoTrocar>()

/**
 * Registers a reaction to a tenant switch. Returns the unregister function so a
 * remount cannot leave a stale listener behind.
 *
 * Story 0.5 registers the query-cache invalidation; story 0.4 will register the
 * feature-flag re-resolution.
 */
export function aoTrocarDeCliente(ouvinte: AoTrocar): () => void {
  ouvintes.add(ouvinte)
  return () => {
    ouvintes.delete(ouvinte)
  }
}

/**
 * Runs every listener. One failure must not skip the others.
 *
 * ⚠️ The `async` wrapper is load-bearing, not stylistic. `map(o => o(id))` lets a
 * listener that throws **synchronously** blow up the `map` itself, before
 * `allSettled` is ever called — so the isolation this function advertises would
 * fail in precisely the case it exists for. `async` turns that throw into a
 * rejected promise, which is what `allSettled` can actually absorb.
 */
export async function notificarTrocaDeCliente(idCliente: string): Promise<void> {
  await Promise.allSettled([...ouvintes].map(async (ouvinte) => ouvinte(idCliente)))
}

/** Test seam — drops every registration so one test cannot leak into the next. */
export function reiniciarTrocaParaTeste(): void {
  ouvintes.clear()
}
